import React, { useEffect, useState } from 'react';
import { Splitter, Select, Card, Typography, Row, Col, message, Spin } from 'antd';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Tooltip, Legend } from 'recharts';

import axiosInstance from '../../utils/axiosInstance';

const { Option } = Select;
const { Text } = Typography;

// Color constants
const HIGHLIGHT_COLOR = '#C7F6C7';
const COLORS = ['#465892', '#546DA6', '#BBC1C6', '#968EAF', '#6D5593', '#543D7B'];


// Grouped list of stats for comparison
const groupedStats = [
  {
    group: "Points & Shooting",
    stats: [
      { key: "pts", label: "Points" },
      { key: "fga", label: "Field Goals Attempted" },
      { key: "fgm", label: "Field Goals Made" },
      { key: "three_pa", label: "3-Point Field Goals Attempted" },
      { key: "three_pm", label: "3-Point Field Goals Made" },
      { key: "fta", label: "Free Throw Attempted" },
      { key: "ftm", label: "Free Throws Made" },
    ],
  },
  {
    group: "Shooting Percentages",
    stats: [
      { key: "fg_pct", label: "Field Goal %", isPercentage: true },
      { key: "three_pct", label: "3-Point Field Goal %", isPercentage: true },
      { key: "ft_pct", label: "Free Throw %", isPercentage: true },
      { key: "ts_pct", label: "True Shooting %", isPercentage: true },
      { key: "efg_pct", label: "Effective Field Goal %", isPercentage: true },
    ],
  },
  {
    group: "Rebounds",
    stats: [
      { key: "reb", label: "Rebounds" },
      { key: "oreb", label: "Offensive Rebounds" },
      { key: "oreb_pct", label: "Offensive Rebound %", isPercentage: true },
      { key: "dreb", label: "Defensive Rebounds" },
      { key: "dreb_pct", label: "Defensive Rebound %", isPercentage: true },
    ],
  },
  {
    group: "Playmaking",
    stats: [
      { key: "ast", label: "Assists" },
      { key: "tov", label: "Turnovers", isLowerBetter: true },
      { key: "tov_pct", label: "Turnover %", isPercentage: true, isLowerBetter: true },
      { key: "ast_to", label: "Assist-to-Turnover Ratio" },
    ],
  },
  {
    group: "Efficiency & Production",
    stats: [
      { key: "poss", label: "Possessions" },
      { key: "usg_pct", label: "Usage Rate", isPercentage: true },
      { key: "oe", label: "Offensive Efficiency" },
      { key: "eps", label: "Efficient Points Scored" },
      { key: "eop", label: "Efficient Offensive Production" },
    ],
  },
];

// Formatters for percentages and decimals
const percentageFormatter = (value) => `${(value * 100).toFixed(2)}%`;
const decimalFormatter = (value) => {
  if (Number.isInteger(value)) {
    return value.toFixed(0);
  }
  return value?.toFixed(2);
};

const PlayerStatsComparison = ({ gameId }) => {
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [player1Stats, setPlayer1Stats] = useState({});
  const [player2Stats, setPlayer2Stats] = useState({});
  const [selectedTeam1, setSelectedTeam1] = useState(null);
  const [selectedPlayer1, setSelectedPlayer1] = useState(null);
  const [selectedTeam2, setSelectedTeam2] = useState(null);
  const [selectedPlayer2, setSelectedPlayer2] = useState(null);
  const [selectedStat, setSelectedStat] = useState(null);
  const [rankedData, setRankedData] = useState([]);
  const [loading, setLoading] = useState(false); // Added loading state

  

  useEffect(() => {
    const fetchTeamsAndPlayers = async () => {
      try {
        const response = await axiosInstance.get(`/api/games/${gameId}/std_player_stats/`);
        const uniqueTeams = [...new Set(response.data.map((stat) => stat.team))];
        setTeams(uniqueTeams);
        setPlayers(response.data);
      } catch (error) {
        message.error("Failed to fetch player stats");
      }
    };
    fetchTeamsAndPlayers();
  }, [gameId]);

  useEffect(() => {
    const fetchAllPlayerStats = async () => {
      try {
        const [stdResponse, derivedResponse, anaResponse] = await Promise.all([
          axiosInstance.get(`/api/games/${gameId}/std_player_stats/`),
          axiosInstance.get(`/api/games/${gameId}/der_player_stats/`),
          axiosInstance.get(`/api/games/${gameId}/ana_player_stats/`),
        ]);
  
        // Merge stats from all sources
        const mergedStats = stdResponse.data.map((stdPlayer) => {
          const derivedPlayer = derivedResponse.data.find(
            (player) => player.player_number === stdPlayer.player_number && player.team === stdPlayer.team
          );
          const anaPlayer = anaResponse.data.find(
            (player) => player.player_number === stdPlayer.player_number && player.team === stdPlayer.team
          );
  
          return {
            ...stdPlayer,
            ...derivedPlayer,
            ...anaPlayer,
          };
        });
  
        setPlayers(mergedStats); // Store all merged player stats
      } catch (error) {
        message.error("Failed to fetch player stats");
      }
    };
  
    fetchAllPlayerStats();
  }, [gameId]); // Fetch only once when the gameId changes
  
  useEffect(() => {
    if (selectedTeam1 && selectedPlayer1) {
      const selectedPlayerStats = players.find(
        (player) => player.team === selectedTeam1 && player.player_number === selectedPlayer1
      );
      setPlayer1Stats(selectedPlayerStats || {});
    }
  
    if (selectedTeam2 && selectedPlayer2) {
      const selectedPlayerStats = players.find(
        (player) => player.team === selectedTeam2 && player.player_number === selectedPlayer2
      );
      setPlayer2Stats(selectedPlayerStats || {});
    }
  }, [selectedTeam1, selectedPlayer1, selectedTeam2, selectedPlayer2, players]); // Update selected player stats when team or player changes
  
  useEffect(() => {
    if (selectedStat && players.length > 0) {
      const statData = players
        .map((player) => ({
          key: `${player.team}-${player.player_number}`, // Unique key for each player
          name: `Player ${player.player_number}`,
          team: player.team,
          value: player[selectedStat] || 0, // Use the selected stat
        }))
        .sort((a, b) => b.value - a.value); // Sort descending by value
      setRankedData(statData); // Reset ranked data to new selection
    } else {
      setRankedData([]); // Clear table if no stat is selected
    }
  }, [selectedStat, players]); // Update ranked data when selectedStat or players change
  

  const renderComparisonTable = () => (
    <table style={{ width: "100%", textAlign: "center", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <th style={{ padding: "10px", borderBottom: "2px solid #ddd" }}></th>
          <th style={{ padding: "10px", borderBottom: "2px solid #ddd" }}>Player 1</th>
          <th style={{ padding: "10px", borderBottom: "2px solid #ddd" }}>Player 2</th>
        </tr>
      </thead>
      <tbody>
        {groupedStats.map(({ group, stats }) => (
          <React.Fragment key={group}>
            <tr>
              <th
                colSpan="3"
                style={{
                  background: "#f0f0f0",
                  padding: "10px",
                  borderBottom: "2px solid #ddd",
                  fontWeight: "bold",
                }}
              >
                {group}
              </th>
            </tr>
            {stats.map(({ key, label, isPercentage, isLowerBetter }) => {
              const player1Value = player1Stats[key];
              const player2Value = player2Stats[key];

              let highlightPlayer1 = false;
              let highlightPlayer2 = false;

              if (player1Value != null && player2Value != null) {
                if (isLowerBetter) {
                  highlightPlayer1 = player1Value < player2Value;
                  highlightPlayer2 = player2Value < player1Value;
                } else {
                  highlightPlayer1 = player1Value > player2Value;
                  highlightPlayer2 = player2Value > player1Value;
                }
              }

              const equalValues = player1Value === player2Value && player1Value != null;

              return (
                <tr key={key}>
                  <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>{label}</td>
                  <td
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #ddd",
                      backgroundColor: highlightPlayer1 || equalValues ? HIGHLIGHT_COLOR : "transparent",
                    }}
                  >
                    {player1Value != null
                      ? isPercentage
                        ? percentageFormatter(player1Value)
                        : decimalFormatter(player1Value)
                      : "N/A"}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #ddd",
                      backgroundColor: highlightPlayer2 || equalValues ? HIGHLIGHT_COLOR : "transparent",
                    }}
                  >
                    {player2Value != null
                      ? isPercentage
                        ? percentageFormatter(player2Value)
                        : decimalFormatter(player2Value)
                      : "N/A"}
                  </td>
                </tr>
              );
            })}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );


  return (
    <div style={{ padding: '20px' }}>
      {loading ? (
        // Display a centered loading spinner while loading
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
      {/* Player Selection */}
      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col span={12}>
          <Card title="Select Player 1">
            <Select
              placeholder="Select Team"
              style={{ width: '100%', marginBottom: '10px' }}
              onChange={setSelectedTeam1}
            >
              {teams.map((team) => (
                <Option key={team} value={team}>
                  {team}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="Select Player"
              style={{ width: '100%' }}
              onChange={setSelectedPlayer1}
              disabled={!selectedTeam1}
            >
              {players
                .filter((player) => player.team === selectedTeam1)
                .map((player) => (
                  <Option key={player.player_number} value={player.player_number}>
                    Player {player.player_number}
                  </Option>
                ))}
            </Select>
          </Card>
        </Col>

        <Col span={12}>
          <Card title="Select Player 2">
            <Select
              placeholder="Select Team"
              style={{ width: '100%', marginBottom: '10px' }}
              onChange={setSelectedTeam2}
            >
              {teams.map((team) => (
                <Option key={team} value={team}>
                  {team}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="Select Player"
              style={{ width: '100%' }}
              onChange={setSelectedPlayer2}
              disabled={!selectedTeam2}
            >
              {players
                .filter((player) => player.team === selectedTeam2)
                .map((player) => (
                  <Option key={player.player_number} value={player.player_number}>
                    Player {player.player_number}
                  </Option>
                ))}
            </Select>
          </Card>
        </Col>
      </Row>

      {/* Display Splitter with table and right-side panels */}
      {selectedPlayer1 && selectedPlayer2 ? (
        <div style={{ maxHeight: 'auto', overflowY: 'auto' }}>
          <Splitter style={{ height: '100%', display: 'flex', boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)' }}>
            {/* Left Panel with Player Comparison Table */}
            <Splitter.Panel collapsible style={{ flex: 1, minWidth: '300px' }}>
              <Card title="Player Comparison Table" style={{ height: '100%' }}>
                {renderComparisonTable()}
              </Card>
            </Splitter.Panel>

             {/* Right Panel with multiple cards */}
             <Splitter.Panel style={{ flex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                
                {/* Upper Right Panel */}
                <Card 
                  title="Four Factors Analysis" 
                  style={{ flex: 1, marginBottom: '8px'}}
                >
                  <RadarChart
                    cx="50%"
                    cy="50%"
                    outerRadius="80%"
                    width={550} // Ensures responsiveness
                    height={550}
                    data={[
                      {
                        factor: "4F",
                        Player1: player1Stats.four_factors || 0,
                        Player2: player2Stats.four_factors || 0,
                      },
                      {
                        factor: "eFG%",
                        Player1: player1Stats.efg_pct || 0,
                        Player2: player2Stats.efg_pct || 0,
                      },
                      {
                        factor: "OREB%",
                        Player1: player1Stats.oreb_pct || 0,
                        Player2: player2Stats.oreb_pct || 0,
                      },
                      {
                        factor: "TOV%",
                        Player1: player1Stats.tov_pct || 0,
                        Player2: player2Stats.tov_pct || 0,
                      },
                      {
                        factor: "FTF",
                        Player1: player1Stats.ft_factor || 0,
                        Player2: player2Stats.ft_factor || 0,
                      },
                    ]}
                  >
                    <PolarGrid />
                    <PolarAngleAxis dataKey="factor" />
                    <PolarRadiusAxis tickFormatter={decimalFormatter} />
                    <Radar
                      name="Player 1"
                      dataKey="Player1"
                      stroke={COLORS[0]}
                      fill={COLORS[0]}
                      fillOpacity={0.6}
                    />
                    <Radar
                      name="Player 2"
                      dataKey="Player2"
                      stroke={COLORS[2]}
                      fill={COLORS[2]}
                      fillOpacity={0.6}
                    />
                    <Tooltip formatter={decimalFormatter} />
                    <Legend
                      layout="horizontal"
                      verticalAlign="bottom"
                      align="center"
                      formatter={(value) => (
                        <span style={{ color: value === "Player 1" ? COLORS[0] : COLORS[2] }}>
                          {value}
                        </span>
                      )}
                    />
                  </RadarChart>
                </Card>

                <Card title="Player Rankings" style={{ flex: 1 }}>
                  <Select
                    style={{ width: '100%', marginBottom: '10px' }}
                    placeholder="Select a stat to rank players"
                    onChange={(value) => setSelectedStat(value)} // Update selectedStat dynamically
                    value={selectedStat} // Current selection
                  >
                    {groupedStats.flatMap((group) => group.stats).map((stat) => (
                      <Option key={stat.key} value={stat.key}>
                        {stat.label}
                      </Option>
                    ))}
                  </Select>
                  {rankedData.length > 0 ? (
                    <table style={{ width: "100%", textAlign: "center", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th>Player</th>
                          <th>Team</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rankedData.map((record) => (
                          <tr
                            key={record.key}
                            style={{
                              backgroundColor:
                                (record.key === `${selectedTeam1}-${selectedPlayer1}`) ||
                                (record.key === `${selectedTeam2}-${selectedPlayer2}`)
                                  ? COLORS[2]
                                  : undefined,
                            }}
                          >
                            <td>
                              <Text
                                strong={
                                  record.key === `${selectedTeam1}-${selectedPlayer1}` ||
                                  record.key === `${selectedTeam2}-${selectedPlayer2}`
                                }
                              >
                                {record.name}
                              </Text>
                            </td>
                            <td>{record.team}</td>
                            <td>{decimalFormatter(record.value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <Typography.Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
                      No data available for the selected stat.
                    </Typography.Text>
                  )}
                </Card>
              </div>
            </Splitter.Panel>
          </Splitter>
        </div>
      ) : (
        <Text type="secondary" style={{ padding: '20px', textAlign: 'center' }}>
          Select two players to view stats.
        </Text>
      )}
      </>
      )}
    </div>
  );
};

export default PlayerStatsComparison;

